import { useMemo } from 'react';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { MobileDatePicker } from '@mui/x-date-pickers/MobileDatePicker';
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker';

import TextField from '@mui/material/TextField';

import useMediaQuery from 'utils/mediaQueries';

import { dateViews } from 'config/constants';
import { isValidDate } from 'utils/datetimeUtils';

import sx from './DatePicker.styles';

// Rules for views in x-date-pickers:
// Each view has its own "visual component" (thus the name "view"). Specifying a view will display the corresponding component as a popup.
// However, there's a caveat:
// .
// DAY / MONTH / YEAR:
// - if we specify inside the views ['day', 'month', 'year'], the component will display a calendar for "day", a picker for "month"
// and a picker for "year". However, the "day" calendar already contains the current month,
// so in this case we don't need the "month" view inside the array.
// - If we specify inside the views ['day', 'year'], the component will display a calendar for "day" and a picker for "year". We also want to
// get rid of the "year" picker, because the calendar already contains a <Select /> component on the top-left corner, which allows us to
// OPTIONALLY select the year. However, if we remove the "year" view from the array, we will get rid of the "year" picker AND the <Select /> component.
// So, in order to get rid of the "year" picker, but keep the <Select /> component,
// we need to specify the "year" in the array BEFORE the "day" view, as ['year', 'day'].
const viewsInfo = {
  dayMonthYear: {
    views: [dateViews.year, dateViews.day],
    inputFormat: 'dd/MM/yyyy',
    openTo: dateViews.day
  },
  monthYear: {
    views: [dateViews.month, dateViews.year],
    inputFormat: 'MM/yyyy',
    openTo: dateViews.month
  },
  year: {
    views: [dateViews.year],
    inputFormat: 'yyyy',
    openTo: dateViews.year
  }
};

const DatePicker = ({ views: rawViews = viewsInfo.dayMonthYear.views, value, onChange, error = false }) => {
  const isMobile = useMediaQuery();

  const viewsConfig = useMemo(() => {
    let hasDay = false;
    let hasMonth = false;
    let hasYear = false;

    rawViews.forEach(view => {
      if (view === dateViews.day || view === dateViews.date) hasDay = true;
      if (view === dateViews.month) hasMonth = true;
      if (view === dateViews.year) hasYear = true;
    });

    if (!rawViews || rawViews.length < 1) return viewsInfo.dayMonthYear;

    if (hasDay) return viewsInfo.dayMonthYear;
    if (hasMonth) return viewsInfo.monthYear;
    if (hasYear) return viewsInfo.year;

    return viewsInfo.dayMonthYear;
  }, [rawViews]);

  const hasError = useMemo(() => value && !isValidDate(value), [value]) || error;

  const commonProps = {
    value,
    onChange,
    views: viewsConfig.views,
    inputFormat: viewsConfig.inputFormat,
    openTo: viewsConfig.openTo,
    showDaysOutsideCurrentMonth: true,
    disableHighlightToday: true,
    renderInput: params => (
      <TextField
        {...params}
        sx={sx.textField}
        error={hasError}
        data-test="keyboard-date-picker"
        {...(hasError ? { helperText: 'Invalid date format' } : {})}
      />
    )
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      {isMobile ? (
        <MobileDatePicker {...commonProps} DialogProps={{ sx: sx.container }} />
      ) : (
        <DesktopDatePicker {...commonProps} PopperProps={{ sx: sx.container }} />
      )}
    </LocalizationProvider>
  );
};

export default DatePicker;
